import { useToken } from '@chakra-ui/react';

import {
  DivergingBarWithArrows,
  FormatType,
  SENTIMENT_NEGATIVE_COLOR_DARK,
  SENTIMENT_NEGATIVE_COLOR_LIGHT,
  SENTIMENT_POSITIVE_COLOR,
  useChartTheme,
} from '@revelio/replots';

import { usePlotMetric } from '../../../hooks/usePlotMetric';
import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataSet,
} from '../../DataProvider/types';
import { useChart } from '../ChartProvider';
import { getPrettyFormatValue } from '../utils/format';
import { getFormat } from '../utils/getFormat';
import { getDivergingBarConfig } from '../utils/map-to-diverging-data';
import { useActiveColors } from '../utils/useActiveColors';

const NEW_THEME_LAYOUT = {
  labelWidth: 150,
  labelChartGap: 36,
  centerGap: 6,
  barThickness: 14,
  barGap: 16,
} as const;

export const DivergingBar = ({
  dataSet,
}: {
  dataSet: BreakdownSingleDataSet | SingleDataSet;
}) => {
  const { item } = useChart();
  const metric = usePlotMetric();
  const { theme } = useChartTheme();

  const { colorMap } = useActiveColors();

  const format = (() => {
    const plotFormat = item?.plot?.format;
    if (plotFormat !== undefined) {
      return plotFormat;
    }
    if (metric === 'sentiment_top_bottom') {
      return FormatType.PERCENTAGE;
    }
    if (item?.plot?.comparisonTransformation) {
      return FormatType.DECIMAL;
    }
    return getFormat({ metric });
  })();

  const formatValue = getPrettyFormatValue(format);
  const showBarLabels = item?.plot?.showDataLabels ?? false;
  const arrowLabelColor = useToken('colors', 'gray.700');
  const arrowLabelFont = useToken('fonts', 'body');

  const {
    divergingBarData,
    rightColor,
    leftFooterLabel,
    rightFooterLabel,
    tooltipContent,
    valueLabel,
  } = getDivergingBarConfig(dataSet, item, colorMap, formatValue);

  const isComparison = dataSet.type === DataType.BREAKDOWN_SINGLE;
  const comparisonBarColor = theme === 'dark' ? '#45484C' : '#E8E8E8';
  const sentimentNegativeColor =
    theme === 'dark'
      ? SENTIMENT_NEGATIVE_COLOR_DARK
      : SENTIMENT_NEGATIVE_COLOR_LIGHT;
  // A comparison plot greys the baseline side and keeps the configured colour
  // on the focus side; everything else is the sentiment pair.
  const resolvedLeftColor = isComparison
    ? comparisonBarColor
    : sentimentNegativeColor;
  const resolvedRightColor = isComparison
    ? rightColor
    : SENTIMENT_POSITIVE_COLOR;

  const divergingBar = item?.plot?.divergingBar;
  const arrowOrientation = divergingBar?.arrowOrientation ?? 'horizontal';
  const horizontalArrows =
    arrowOrientation === 'horizontal' && (leftFooterLabel || rightFooterLabel)
      ? {
          leftLabel: leftFooterLabel,
          rightLabel: rightFooterLabel,
        }
      : undefined;
  const verticalArrows =
    arrowOrientation === 'vertical' &&
    (divergingBar?.positiveLabel || divergingBar?.negativeLabel)
      ? {
          topLabel: divergingBar?.positiveLabel,
          bottomLabel: divergingBar?.negativeLabel,
          color: arrowLabelColor,
          fontFamily: arrowLabelFont,
        }
      : undefined;

  return (
    <DivergingBarWithArrows
      data={divergingBarData}
      format={format}
      leftColor={resolvedLeftColor}
      rightColor={resolvedRightColor}
      tooltipContent={tooltipContent}
      showBarLabels={showBarLabels}
      valueLabel={valueLabel}
      barLabelPosition="outside"
      horizontalArrows={horizontalArrows}
      verticalArrows={verticalArrows}
      {...NEW_THEME_LAYOUT}
    />
  );
};
